import React from 'react'

import { Controller, FieldError, useFormContext } from 'react-hook-form'

import { ArcadeInput, Input as DefaultInput } from '@cash-design-system/react'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-feature-flags'

import { FieldErrorText, FormInputWrapper } from '../'
type Props = React.ComponentProps<typeof DefaultInput> & {
  transform?: (value: string) => unknown
  name: string
  rules?: React.ComponentProps<typeof Controller>['rules'] // https://react-hook-form.com/api/useform/register#options
  customInput?: React.ComponentType<React.ComponentProps<typeof DefaultInput>>
  showErrors?: boolean
}

export function FormInput({
  name,
  rules,
  customInput,
  disabled,
  showErrors = true,
  transform = v => v,
  ...props
}: Props): React.ReactElement {
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('input')

  const {
    register,
    formState: { dirtyFields, errors, isSubmitted, isSubmitting },
  } = useFormContext()

  const { onBlur, onChange, ...inputProps } = register(name, { ...rules, setValueAs: transform })

  // traverse object to get error message
  const error = name.split('.').reduce((acc, key) => {
    return acc?.[key]
  }, errors) as FieldError

  const showError = error && (error?.type !== 'required' || isSubmitted)

  if (isArcadeMigrationEnabled) {
    const Input = customInput ?? ArcadeInput
    return (
      <FormInputWrapper>
        <Input
          {...props}
          {...inputProps}
          disabled={disabled || isSubmitting}
          onChange={e => {
            props.onChange?.(e)
            onChange(e)
          }}
          onBlur={e => {
            props.onBlur?.(e)
            if (dirtyFields[name]) {
              // validate onBlur only when field is dirty
              onBlur(e)
            }
          }}
          id={props.id ?? name}
          className={showError ? 'error' : ''}
          isInvalid={showError}
          errorText={error?.message}
        />
      </FormInputWrapper>
    )
  }

  const Input = customInput ?? DefaultInput
  return (
    <FormInputWrapper>
      <Input
        {...props}
        {...inputProps}
        disabled={disabled || isSubmitting}
        onChange={e => {
          props.onChange?.(e)
          onChange(e)
        }}
        onBlur={e => {
          props.onBlur?.(e)
          if (dirtyFields[name]) {
            // validate onBlur only when field is dirty
            onBlur(e)
          }
        }}
        id={props.id ?? name}
        className={showError ? 'error' : ''}
      />
      {showErrors && <FieldErrorText name={name} />}
    </FormInputWrapper>
  )
}
